import { useState } from "react";
import { Link } from "react-router-dom";
import {
  attendanceAnalytics,
  attendanceDetails,
  type AttendanceAnalyticsReport,
  type AttendanceMetric,
  type AttendanceStudentFilter,
} from "../../booking/attendanceAnalytics.js";
import type { ReportQuery } from "../../booking/staff.js";
import { formatDate } from "../../format.js";
import { useAsync } from "../../useAsync.js";
import "./attendanceAnalytics.css";

interface AttendanceAnalyticsProps {
  query: ReportQuery;
  chosen: readonly number[] | null;
  names: Map<number, string>;
}

interface DrillSelection {
  portalStudentId?: string;
  metric: AttendanceMetric;
  label: string;
}

function reportKey(query: ReportQuery): string {
  return `${query.start_date}:${query.end_date}:${query.scope}:${query.provider_id ?? "all"}`;
}

export function AttendanceAnalytics({ query, chosen, names }: AttendanceAnalyticsProps) {
  if (chosen === null) {
    return (
      <AttendanceReport
        key={reportKey(query)}
        query={query}
        names={names}
        title="Attendance"
      />
    );
  }

  return (
    <section className="attendance-provider-stack" aria-label="Attendance by tutor">
      {chosen.map((providerId) => {
        const providerQuery = { ...query, provider_id: providerId };
        return (
          <AttendanceReport
            key={reportKey(providerQuery)}
            query={providerQuery}
            names={names}
            title={`Attendance · ${names.get(providerId) ?? `Tutor ${providerId}`}`}
          />
        );
      })}
    </section>
  );
}

function AttendanceReport({
  query,
  names,
  title,
}: {
  query: ReportQuery;
  names: Map<number, string>;
  title: string;
}) {
  const [studentFilter, setStudentFilter] = useState<AttendanceStudentFilter>("all");
  const [studentPage, setStudentPage] = useState(1);
  const [selection, setSelection] = useState<DrillSelection | null>(null);
  const result = useAsync(
    () => attendanceAnalytics(query, {
      studentFilter,
      studentPage,
      studentPageSize: 25,
    }),
    [
      query.start_date,
      query.end_date,
      query.scope,
      query.provider_id,
      studentFilter,
      studentPage,
    ],
    { reloadOnMirrorRefresh: true },
  );
  const idSuffix = String(query.provider_id ?? "all");

  if (result.loading && result.data === null) {
    return (
      <section className="panel attendance-analytics" aria-busy="true">
        <h2>{title}</h2>
        <p className="muted">Loading attendance…</p>
      </section>
    );
  }

  if (result.error && result.data === null) {
    return (
      <section className="panel attendance-analytics">
        <h2>{title}</h2>
        <p className="empty">Attendance could not be loaded. {result.error}</p>
        <button type="button" onClick={result.reload}>Try again</button>
      </section>
    );
  }

  const data = result.data;
  if (data === null) return null;
  const studentTotalPages = Math.max(1, Math.ceil(data.by_student_total / data.by_student_page_size));

  const chooseStudentFilter = (next: AttendanceStudentFilter) => {
    setStudentFilter(next);
    setStudentPage(1);
  };

  return (
    <section className="panel attendance-analytics" aria-labelledby={`attendance-${idSuffix}`}>
      <div className="attendance-heading">
        <div>
          <h2 id={`attendance-${idSuffix}`}>{title}</h2>
          <p className="small muted">{data.summary.coverage_note}</p>
        </div>
        {result.loading && <span className="small muted">Refreshing…</span>}
      </div>

      <Summary data={data} onDrill={setSelection} />

      <p className="small muted attendance-denominator-note">{data.summary.denominator_note}</p>

      <Trend data={data} idSuffix={idSuffix} />

      <section className="attendance-students" aria-labelledby={`attendance-students-${idSuffix}`}>
        <div className="attendance-subheading">
          <div>
            <h3 id={`attendance-students-${idSuffix}`}>By student</h3>
            <p className="small muted">
              Thresholds are server-side filters only; they are not risk scores. Late lessons are
              included in Attended.
            </p>
          </div>
          <div className="attendance-filter-group" role="group" aria-label="Filter students by attendance pattern">
            <button
              type="button"
              className={studentFilter === "all" ? "active" : undefined}
              aria-pressed={studentFilter === "all"}
              onClick={() => chooseStudentFilter("all")}
            >
              All
            </button>
            <button
              type="button"
              className={studentFilter === "no_show" ? "active" : undefined}
              aria-pressed={studentFilter === "no_show"}
              onClick={() => chooseStudentFilter("no_show")}
            >
              2+ no-shows
            </button>
            <button
              type="button"
              className={studentFilter === "cancelled" ? "active" : undefined}
              aria-pressed={studentFilter === "cancelled"}
              onClick={() => chooseStudentFilter("cancelled")}
            >
              3+ cancellations
            </button>
          </div>
        </div>

        {data.by_student.length === 0 ? (
          <p className="empty">
            {studentFilter === "all"
              ? "No lessons fall in this attendance period."
              : "No students match this filter in the selected period."}
          </p>
        ) : (
          <div className="attendance-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th className="tabular">Lessons</th>
                  <th className="tabular">Attended</th>
                  <th className="tabular">Late</th>
                  <th className="tabular">Cancelled</th>
                  <th className="tabular">No-show</th>
                  <th>Last lesson</th>
                </tr>
              </thead>
              <tbody>
                {data.by_student.map((student) => (
                  <tr key={student.portal_student_id}>
                    <td>
                      <button
                        type="button"
                        className="attendance-table-link"
                        onClick={() => setSelection({
                          portalStudentId: student.portal_student_id,
                          metric: "all",
                          label: student.student_name ?? "Student lessons",
                        })}
                      >
                        {student.student_name ?? "Student unavailable"}
                      </button>
                    </td>
                    <td className="tabular">{student.scheduled}</td>
                    <td className="tabular">
                      <MetricButton
                        value={student.attended}
                        label={`Show attended lessons for ${student.student_name ?? "student"}`}
                        onClick={() => setSelection({
                          portalStudentId: student.portal_student_id,
                          metric: "attended",
                          label: `${student.student_name ?? "Student"} · attended`,
                        })}
                      />
                    </td>
                    <td className="tabular">
                      <MetricButton
                        value={student.late}
                        label={`Show late lessons for ${student.student_name ?? "student"}`}
                        onClick={() => setSelection({
                          portalStudentId: student.portal_student_id,
                          metric: "late",
                          label: `${student.student_name ?? "Student"} · late`,
                        })}
                      />
                    </td>
                    <td className="tabular">
                      <MetricButton
                        value={student.cancelled}
                        label={`Show cancelled lessons for ${student.student_name ?? "student"}`}
                        onClick={() => setSelection({
                          portalStudentId: student.portal_student_id,
                          metric: "cancelled",
                          label: `${student.student_name ?? "Student"} · cancelled`,
                        })}
                      />
                    </td>
                    <td className="tabular">
                      <MetricButton
                        value={student.no_show}
                        label={`Show no-shows for ${student.student_name ?? "student"}`}
                        onClick={() => setSelection({
                          portalStudentId: student.portal_student_id,
                          metric: "no_show",
                          label: `${student.student_name ?? "Student"} · no-shows`,
                        })}
                      />
                    </td>
                    <td>{student.last_lesson_at ? formatDate(student.last_lesson_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.by_student_total > data.by_student_page_size && (
          <div className="attendance-pagination" aria-label="Attendance student pages">
            <button
              type="button"
              disabled={studentPage <= 1}
              onClick={() => setStudentPage((value) => Math.max(1, value - 1))}
            >
              Previous
            </button>
            <span className="small muted">
              Page {studentPage} of {studentTotalPages} · {data.by_student_total} students
            </span>
            <button
              type="button"
              disabled={studentPage >= studentTotalPages}
              onClick={() => setStudentPage((value) => value + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      {selection && (
        <Drilldown
          key={`${reportKey(query)}:${selection.portalStudentId ?? "all"}:${selection.metric}`}
          query={query}
          names={names}
          selection={selection}
          idSuffix={idSuffix}
          onClose={() => setSelection(null)}
        />
      )}

      <Coverage sources={data.data_sources} />
    </section>
  );
}

function Summary({
  data,
  onDrill,
}: {
  data: AttendanceAnalyticsReport;
  onDrill: (selection: DrillSelection) => void;
}) {
  const { summary } = data;
  return (
    <>
      <div className="stats attendance-stats">
        <SummaryStat
          label="Lessons in range"
          value={String(summary.scheduled)}
          note="Total; the outcome counts below partition this figure"
          onClick={() => onDrill({ metric: "all", label: "All contributing lessons" })}
        />
        <SummaryStat
          label="Attendance rate"
          value={percentage(summary.attendance_rate_percent)}
          note={`Denominator: ${summary.attendance_rate_denominator}`}
          onClick={() => onDrill({ metric: "attended", label: "Lessons counted as attended" })}
        />
        <SummaryStat
          label="Cancellation rate"
          value={percentage(summary.cancellation_rate_percent)}
          note={`Denominator: ${summary.cancellation_rate_denominator}`}
          onClick={() => onDrill({ metric: "cancelled", label: "Cancelled lessons" })}
        />
        <SummaryStat
          label="No-show rate"
          value={percentage(summary.no_show_rate_percent)}
          note={`Denominator: ${summary.no_show_rate_denominator}`}
          onClick={() => onDrill({ metric: "no_show", label: "No-show lessons" })}
        />
      </div>

      <h3 className="attendance-outcome-heading">Outcome breakdown</h3>
      <div className="stats attendance-stats">
        <SummaryStat
          label="Attended"
          value={String(summary.attended)}
          note={`${summary.late} late included`}
          onClick={() => onDrill({ metric: "attended", label: "Attended lessons" })}
        />
        <SummaryStat
          label="Cancelled"
          value={String(summary.cancelled)}
          onClick={() => onDrill({ metric: "cancelled", label: "Cancelled lessons" })}
        />
        <SummaryStat
          label="No-show"
          value={String(summary.no_show)}
          onClick={() => onDrill({ metric: "no_show", label: "No-show lessons" })}
        />
        <SummaryStat
          label="Other / undecided"
          value={String(summary.other)}
          note="Includes future scheduled and unclassified lessons"
          onClick={() => onDrill({ metric: "other", label: "Other or undecided lessons" })}
        />
      </div>
    </>
  );
}

function SummaryStat({
  label,
  value,
  note,
  onClick,
}: {
  label: string;
  value: string;
  note?: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="stat attendance-stat" onClick={onClick}>
      <span className="stat-value tabular">{value}</span>
      <span className="stat-label">{label}</span>
      {note && <span className="stat-note">{note}</span>}
    </button>
  );
}

function Trend({ data, idSuffix }: { data: AttendanceAnalyticsReport; idSuffix: string }) {
  const maxScheduled = Math.max(1, ...data.trend.map((row) => row.scheduled));
  const granularity = data.trend_granularity === "day" ? "Daily" : "Weekly";
  const headingId = `attendance-trend-${idSuffix}`;

  return (
    <section className="attendance-trend" aria-labelledby={headingId}>
      <div className="attendance-subheading">
        <div>
          <h3 id={headingId}>Trend</h3>
          <p className="small muted">{granularity} buckets in Europe/London. Late is included in Attended.</p>
        </div>
      </div>

      {data.trend.length === 0 ? (
        <p className="empty">There are no lessons to plot in this period.</p>
      ) : (
        <>
          <div className="attendance-chart" aria-hidden="true">
            {data.trend.map((row) => (
              <div className="attendance-chart-row" key={`${row.bucket_start}:${row.bucket_end}`}>
                <span className="attendance-chart-label">{shortPeriod(row.bucket_start, row.bucket_end)}</span>
                <span className="attendance-chart-track">
                  <span
                    className="attendance-chart-bar attended"
                    style={{ width: `${(row.attended / maxScheduled) * 100}%` }}
                  />
                  <span
                    className="attendance-chart-bar no-show"
                    style={{ width: `${(row.no_show / maxScheduled) * 100}%` }}
                  />
                  <span
                    className="attendance-chart-bar cancelled"
                    style={{ width: `${(row.cancelled / maxScheduled) * 100}%` }}
                  />
                </span>
              </div>
            ))}
          </div>

          <div className="attendance-table-wrap">
            <table>
              <caption className="sr-only">Attendance trend data equivalent to the chart</caption>
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="tabular">Lessons</th>
                  <th className="tabular">Attended</th>
                  <th className="tabular">Late</th>
                  <th className="tabular">Cancelled</th>
                  <th className="tabular">No-show</th>
                  <th className="tabular">Other</th>
                  <th className="tabular">Attendance</th>
                </tr>
              </thead>
              <tbody>
                {data.trend.map((row) => (
                  <tr key={`${row.bucket_start}:${row.bucket_end}`}>
                    <td>{shortPeriod(row.bucket_start, row.bucket_end)}</td>
                    <td className="tabular">{row.scheduled}</td>
                    <td className="tabular">{row.attended}</td>
                    <td className="tabular">{row.late}</td>
                    <td className="tabular">{row.cancelled}</td>
                    <td className="tabular">{row.no_show}</td>
                    <td className="tabular">{row.other}</td>
                    <td className="tabular">{percentage(row.attendance_rate_percent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Drilldown({
  query,
  names,
  selection,
  idSuffix,
  onClose,
}: {
  query: ReportQuery;
  names: Map<number, string>;
  selection: DrillSelection;
  idSuffix: string;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const result = useAsync(
    () => attendanceDetails(query, {
      portalStudentId: selection.portalStudentId,
      metric: selection.metric,
      page,
      pageSize: 25,
    }),
    [
      query.start_date,
      query.end_date,
      query.scope,
      query.provider_id,
      selection.portalStudentId,
      selection.metric,
      page,
    ],
    { reloadOnMirrorRefresh: true },
  );

  const data = result.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const headingId = `attendance-drilldown-${idSuffix}`;

  return (
    <section className="attendance-drilldown" aria-labelledby={headingId}>
      <div className="attendance-subheading">
        <div>
          <h3 id={headingId}>{selection.label}</h3>
          {data && <p className="small muted">{data.total} contributing {data.total === 1 ? "lesson" : "lessons"}.</p>}
        </div>
        <button type="button" onClick={onClose}>Close</button>
      </div>

      {result.loading && data === null && <p className="muted">Loading lessons…</p>}
      {result.error && data === null && <p className="empty">Lesson detail could not be loaded. {result.error}</p>}
      {data && data.items.length === 0 && <p className="empty">No lessons match this drill-down.</p>}
      {data && data.items.length > 0 && (
        <div className="attendance-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Student</th>
                <th>Tutor</th>
                <th>Service</th>
                <th>Status</th>
                <th>Record</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((lesson) => (
                <tr key={`${lesson.hub_appointment_id}:${lesson.portal_student_id}`}>
                  <td>{formatDate(lesson.starts_at)}</td>
                  <td>{lesson.student_name ?? "Student unavailable"}</td>
                  <td>{lesson.provider_id ? names.get(lesson.provider_id) ?? `Tutor ${lesson.provider_id}` : "—"}</td>
                  <td>{lesson.service_name ?? (lesson.service_id ? `Service ${lesson.service_id}` : "—")}</td>
                  <td>{statusLabel(lesson.attendance_status)}</td>
                  <td>
                    {lesson.student_name ? (
                      <Link to={`/students/${encodeURIComponent(lesson.portal_student_id)}`}>
                        Student record
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && totalPages > 1 && (
        <div className="attendance-pagination" aria-label="Attendance lesson pages">
          <button type="button" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
            Previous
          </button>
          <span className="small muted">Page {page} of {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>
            Next
          </button>
        </div>
      )}
    </section>
  );
}

function MetricButton({ value, label, onClick }: { value: number; label: string; onClick: () => void }) {
  return (
    <button type="button" className="attendance-number-link tabular" aria-label={label} onClick={onClick}>
      {value}
    </button>
  );
}

function Coverage({ sources }: { sources: AttendanceAnalyticsReport["data_sources"] }) {
  if (sources.length === 0) return null;
  return (
    <details className="fold attendance-coverage">
      <summary>Data coverage</summary>
      <ul>
        {sources.map((source) => (
          <li key={source.name}>
            <strong>{source.name === "wmf_hub" ? "WMF Hub" : source.name}</strong>: {source.coverage_note}
          </li>
        ))}
      </ul>
    </details>
  );
}

function percentage(value: string | null): string {
  return value === null ? "—" : `${value}%`;
}

function shortPeriod(start: string, end: string): string {
  const startText = formatDate(`${start}T12:00:00Z`);
  if (start === end) return startText;
  return `${startText} – ${formatDate(`${end}T12:00:00Z`)}`;
}

function statusLabel(status: Exclude<AttendanceMetric, "all">): string {
  if (status === "no_show") return "No-show";
  if (status === "cancelled") return "Cancelled";
  if (status === "attended") return "Attended";
  if (status === "late") return "Late";
  if (status === "scheduled") return "Scheduled";
  return "Other";
}
